import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ExternalLink, 
  ArrowLeft, 
  Tag, 
  Download, 
  Star, 
  ShieldCheck, 
  Info, 
  Globe, 
  Smartphone,
  ChevronRight,
  Maximize2,
  Layout as WindowsIcon,
  Play as AndroidIcon,
  Apple as AppleIcon,
  CheckCircle2,
  Lock
} from 'lucide-react';
import { api } from '../utils/api';

const WindowsIconCustom = ({ size = 20 }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="currentColor"
  >
    <path d="M0 3.449L9.75 2.1V11.4H0V3.449zm0 8.851h9.75v9.3L0 20.250V12.3zm10.55-10.2L24 0v11.4h-13.45V2.150zM24 12.3V24l-13.45-1.9V12.3H24z"></path>
  </svg>
);

const AndroidIconCustom = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.523 15.3414c-.5511 0-.9993-.4486-.9993-.9997s.4482-.9997.9993-.9997c.5511 0 .9993.4486.9993.9997s-.4482.9997-.9993.9997m-11.046 0c-.5511 0-.9993-.4486-.9993-.9997s.4482-.9997.9993-.9997c.5511 0 .9993.4486.9993.9997s-.4482.9997-.9993.9997m11.4045-6.0206l1.9973-3.4592a.4158.4158 0 00-.1521-.5676.4164.4164 0 00-.5682.152l-2.0223 3.503C15.5413 8.356 13.8732 7.6445 12 7.6445c-1.8732 0-3.5413.7115-5.1322 1.3045l-2.0223-3.503a.4164.4164 0 00-.5682-.152.4158.4158 0 00-.1521.5676l1.9973 3.4592C2.6841 11.233.5519 14.2407.5519 17.7073h22.8962c0-3.4666-2.1322-6.4743-5.5587-8.3865"></path>
  </svg>
);

const AppleIconCustom = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.057 12.783c.026 2.604 2.235 3.478 2.262 3.49-.021.05-.354 1.205-1.166 2.39-.702 1.025-1.431 2.043-2.57 2.063-1.12.02-1.482-.662-2.759-.662-1.278 0-1.677.641-2.738.682-1.102.04-1.921-1.087-2.628-2.112-1.446-2.095-2.541-5.914-1.052-8.504.74-1.285 2.06-2.097 3.491-2.12 1.085-.017 2.112.737 2.778.737.666 0 1.91-.914 3.204-.783 1.229.053 2.153.303 3.012 1.183-2.67.625-3.155 4.02-1.846 5.646zM13.97 4.144c.542-.658.91-1.57.811-2.484-.794.032-1.751.529-2.32 1.187-.51.585-.956 1.516-.836 2.41.884.068 1.785-.436 2.345-1.113z"></path>
  </svg>
);

export const Apps = () => {
  const [apps, setApps] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const loadApps = async () => {
      try {
        const res = await api.apps.getAll(selectedCategory);
        setApps(res.data);
        const cats = [...new Set(res.data.map(app => app.category))];
        setCategories(cats);
      } catch (error) {
        console.error('Error loading apps:', error);
      } finally {
        setLoading(false);
      }
    };
    loadApps();
  }, [selectedCategory]);
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="w-12 h-12 border border-white/10 rounded-full flex items-center justify-center animate-spin">
          <div className="w-2 h-2 rounded-full bg-[#C6FF00]"></div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen pt-20 bg-black selection:bg-[#C6FF00] selection:text-black">
      {/* Header */}
      <section className="relative py-24 border-b border-white/5 overflow-hidden grid-bg">
        <div className="absolute inset-0 mesh-gradient opacity-20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center md:text-left">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <span className="text-[#C6FF00] font-black tracking-[0.4em] uppercase text-[10px] mb-4 block">Módulos del Ecosistema</span>
            <h1 className="text-6xl sm:text-8xl font-black tracking-tighter mb-8 leading-none italic">
              NEXUS<br />
              <span className="text-gradient">STORE</span>
            </h1>
            <p className="text-xl text-white/50 max-w-2xl font-medium tracking-tight">
              Una selección curada de aplicaciones de élite construidas bajo el framework de Build Brains.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {categories.length > 0 && (
          <div className="flex flex-wrap gap-4 mb-16 overflow-x-auto pb-4 scrollbar-hide py-2">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-8 py-3 rounded-full text-[10px] font-black uppercase tracking-widest transition-all duration-500 whitespace-nowrap border ${
                selectedCategory === null
                  ? 'bg-[#C6FF00] text-black border-[#C6FF00] shadow-[0_0_20px_rgba(198,255,0,0.2)]'
                  : 'bg-white/5 text-white/40 border-white/10 hover:border-[#C6FF00]/50'
              }`}
            >
              Todos los Modelos
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-8 py-3 rounded-full text-[10px] font-black uppercase tracking-widest transition-all duration-500 whitespace-nowrap border ${
                  selectedCategory === cat
                    ? 'bg-[#C6FF00] text-black border-[#C6FF00] shadow-[0_0_20px_rgba(198,255,0,0.2)]'
                    : 'bg-white/5 text-white/40 border-white/10 hover:border-[#C6FF00]/50'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        )}
        
        {apps.length === 0 ? (
          <div className="text-center text-white/20 py-32 border border-white/5 rounded-[4rem] bg-white/[0.02]">
            <p className="text-sm font-black uppercase tracking-widest">No se encontraron módulos activos en este sector.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {apps.map((app, index) => (
              <motion.div
                key={app.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="group relative bg-[#050505] rounded-[2.5rem] p-5 border border-white/5 hover:border-[#C6FF00]/30 transition-all duration-500 shadow-2xl overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[#C6FF00]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                
                <Link to={`/apps/${app.id}`} className="relative z-10 block">
                  <div className="aspect-square rounded-[2rem] bg-black overflow-hidden mb-6 relative border border-white/5">
                    {app.image_url ? (
                      <img
                        src={app.image_url}
                        alt={app.name}
                        className="w-full h-full object-cover grayscale opacity-80 group-hover:grayscale-0 group-hover:opacity-100 group-hover:scale-105 transition-all duration-1000"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center mesh-gradient opacity-10">
                        <Smartphone size={48} className="text-white/20" />
                      </div>
                    )}
                    {app.featured && (
                      <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-black/80 backdrop-blur-xl border border-[#C6FF00]/30 text-[#C6FF00] text-[9px] font-black uppercase tracking-widest">
                        Core
                      </div>
                    )}
                  </div>
                  
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-black text-white text-xl truncate pr-2 group-hover:text-[#C6FF00] transition-colors tracking-tighter">
                      {app.name}
                    </h3>
                    <div className="flex items-center text-white/50 text-xs font-bold">
                      <Star className="w-3 h-3 text-[#C6FF00] fill-[#C6FF00] mr-1" />
                      <span>{app.rating || '4.9'}</span>
                    </div>
                  </div>
                  
                  <p className="text-white/30 text-[10px] font-black uppercase tracking-widest mb-6">
                    {app.category} • {app.size || '32 MB'}
                  </p>
                  
                  <div className="flex items-center justify-between border-t border-white/5 pt-5">
                    <span className="text-[#C6FF00] font-black text-sm tracking-tighter">
                      {app.price_type === 'paid' ? `$${app.price}` : 'ACCESO GRATUITO'}
                    </span>
                    <div className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-white group-hover:bg-[#C6FF00] group-hover:text-black group-hover:border-[#C6FF00] transition-all">
                      <ChevronRight size={18} />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const AppDetail = () => {
  const { id } = useParams();
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(null);
  
  useEffect(() => {
    const loadApp = async () => {
      try {
        const res = await api.apps.getById(id);
        const data = res.data;
        setApp(data);
        if (data.image_url) setActiveImage(data.image_url);
        else if (data.gallery?.length > 0) setActiveImage(data.gallery[0]);
      } catch (error) {
        console.error('Error loading app:', error);
      } finally {
        setLoading(false);
      }
    };
    loadApp();
    window.scrollTo(0, 0);
  }, [id]);
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="w-12 h-12 border border-white/10 rounded-full flex items-center justify-center animate-spin">
          <div className="w-2 h-2 rounded-full bg-[#C6FF00]"></div>
        </div>
      </div>
    );
  }
  
  if (!app) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-black text-white">
        <h2 className="text-2xl font-black mb-8 italic tracking-tighter uppercase text-white/20">Módulo No Disponible</h2>
        <Link to="/apps" className="px-10 py-3 bg-[#C6FF00] text-black font-black uppercase tracking-widest text-xs rounded-full">Regresar a Nexus</Link>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-black text-white pt-20 pb-20 selection:bg-[#C6FF00] selection:text-black">
      {/* Header Section */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <Link to="/apps" className="inline-flex items-center text-white/30 hover:text-[#C6FF00] mb-12 transition-colors text-[10px] font-black uppercase tracking-[0.4em]">
          <ArrowLeft size={12} className="mr-3" />
          <span>Ecosystem</span>
        </Link>

        <div className="flex flex-col md:flex-row gap-12 items-start mb-20 relative">
          <div className="absolute -top-20 -left-20 w-64 h-64 bg-[#C6FF00]/10 blur-[100px] pointer-events-none"></div>
          
          {/* App Icon */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-40 h-40 sm:w-56 sm:h-56 rounded-[3.5rem] bg-[#050505] border border-white/10 overflow-hidden shadow-[0_40px_100px_rgba(0,0,0,0.8)] flex-shrink-0 relative group"
          >
            <img 
              src={app.image_url || '/placeholder-app.png'} 
              alt={app.name} 
              className="w-full h-full object-cover grayscale opacity-80 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-1000" 
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
          </motion.div>

          {/* Info & CTA */}
          <div className="flex-grow pt-4">
            <motion.h1 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="text-5xl sm:text-7xl font-black text-white mb-4 tracking-tighter leading-none italic"
            >
              {app.name}
            </motion.h1>
            <p className="text-[#C6FF00] text-sm font-black uppercase tracking-[0.3em] mb-10">{app.category}</p>
            
            <div className="flex flex-wrap gap-6 items-center">
              <a 
                href={app.demo_url || '#'} 
                target="_blank" 
                rel="noreferrer"
                className="flex items-center justify-center px-12 py-4 bg-[#C6FF00] hover:scale-105 text-black font-black uppercase tracking-[0.2em] text-xs rounded-full transition-all shadow-[0_20px_40px_rgba(198,255,0,0.2)]"
              >
                <Download size={18} className="mr-3" />
                Initialize
              </a>
              <button className="w-12 h-12 rounded-full border border-white/10 text-white/30 hover:text-[#C6FF00] hover:border-[#C6FF00]/30 transition-all flex items-center justify-center bg-white/[0.02]">
                <Globe size={18} />
              </button>
            </div>
          </div>

          {/* Technical Stats */}
          <div className="grid grid-cols-3 gap-12 w-full md:w-auto pt-10 md:pt-4 border-t border-white/5 md:border-t-0">
            <div className="text-center">
              <div className="flex items-center justify-center text-white font-black text-xl italic mb-1 tracking-tighter">
                {app.rating || '4.9'} <Star size={14} className="ml-2 text-[#C6FF00] fill-[#C6FF00]" />
              </div>
              <div className="text-white/20 text-[9px] font-black uppercase tracking-widest">Quality</div>
            </div>
            <div className="text-center border-x border-white/5 px-10">
              <div className="text-white font-black text-xl italic mb-1 tracking-tighter">{app.downloads || '12K'}</div>
              <div className="text-white/20 text-[9px] font-black uppercase tracking-widest">Nodes</div>
            </div>
            <div className="text-center">
              <div className="text-white font-black text-xl italic mb-1 tracking-tighter">{app.size || '18M'}</div>
              <div className="text-white/20 text-[9px] font-black uppercase tracking-widest">Weight</div>
            </div>
          </div>
        </div>

        {/* Gallery */}
        {app.gallery && app.gallery.length > 0 && (
          <div className="mb-24 px-2">
            <h2 className="text-[10px] font-black uppercase tracking-[0.4em] text-white/20 mb-10">Visual Architecture</h2>
            <div className="flex gap-8 overflow-x-auto pb-10 scrollbar-hide snap-x">
              {app.gallery.map((img, idx) => (
                <motion.div 
                  key={idx}
                  whileHover={{ scale: 1.02 }}
                  className="flex-shrink-0 w-80 h-[560px] rounded-[3.5rem] overflow-hidden bg-[#030303] border border-white/5 snap-start shadow-2xl"
                >
                  <img src={img} alt={`Signal ${idx}`} className="w-full h-full object-cover grayscale opacity-80 hover:grayscale-0 hover:opacity-100 transition-all duration-700" />
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-24">
          <div className="lg:col-span-2 space-y-20">
            <section>
              <h2 className="text-3xl font-black text-white mb-8 tracking-tighter uppercase italic">Signal Intelligence</h2>
              <div className="text-white/50 text-xl font-medium leading-relaxed tracking-tight">
                <p className="whitespace-pre-wrap">{app.description}</p>
              </div>
            </section>

            <section className="p-10 rounded-[3rem] bg-[#030303] border border-white/5 flex items-start gap-8 relative overflow-hidden group">
              <div className="absolute inset-0 bg-[#C6FF00]/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="p-5 rounded-3xl bg-[#C6FF00]/10 text-[#C6FF00] relative z-10">
                <ShieldCheck size={32} />
              </div>
              <div className="relative z-10">
                <h4 className="text-xl font-black text-white mb-2 uppercase tracking-tight">Vulnerability Scan: PASS</h4>
                <p className="text-white/30 font-medium">This module has been verified by Build Brains Security Units. No malicious telemetry detected.</p>
              </div>
            </section>

            {/* Permissions */}
            {(app.permissions && app.permissions.length > 0) && (
              <section>
                <div className="flex items-center gap-4 mb-8">
                  <Lock size={20} className="text-[#C6FF00]" />
                  <h3 className="text-2xl font-black text-white uppercase tracking-tighter italic">Access Protocols</h3>
                </div>
                <div className="flex flex-wrap gap-4">
                  {app.permissions.map((permission, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center gap-3 px-6 py-3 rounded-full bg-white/[0.02] border border-white/5 text-white/40 text-[10px] font-black uppercase tracking-widest hover:border-[#C6FF00]/30 transition-colors"
                    >
                      <CheckCircle2 size={14} className="text-[#C6FF00]" />
                      <span>{permission}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Sidebar */}
          <aside className="space-y-12">
            <div className="p-10 rounded-[3.5rem] bg-[#030303] border border-white/10 relative overflow-hidden">
              <div className="absolute inset-0 grid-bg opacity-20"></div>
              <h3 className="text-white font-black mb-10 flex items-center text-xs uppercase tracking-[0.3em] relative z-10">
                <Info size={16} className="mr-3 text-[#C6FF00]" />
                Specifications
              </h3>
              
              <div className="space-y-8 relative z-10">
                <div>
                  <div className="text-white/20 text-[9px] font-black uppercase tracking-[0.2em] mb-2">Build Version</div>
                  <div className="text-white font-black italic tracking-tight">{app.version || '1.0.4'}</div>
                </div>
                <div>
                  <div className="text-white/20 text-[9px] font-black uppercase tracking-[0.2em] mb-2">Last Sync</div>
                  <div className="text-white font-black italic tracking-tight">
                    {new Date(app.updated_at || app.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                </div>
                <div>
                  <div className="text-white/20 text-[9px] font-black uppercase tracking-[0.2em] mb-4">Architecture</div>
                  <div className="flex gap-4">
                    {app.os?.map(os => {
                      const lowerOs = os.toLowerCase();
                      if (lowerOs.includes('win')) return <div key={os} className="w-10 h-10 rounded-2xl bg-white/5 border border-white/10 text-white hover:text-[#C6FF00] hover:border-[#C6FF00]/30 transition-all flex items-center justify-center" title="Windows"><WindowsIconCustom size={18} /></div>;
                      if (lowerOs.includes('and')) return <div key={os} className="w-10 h-10 rounded-2xl bg-white/5 border border-white/10 text-white hover:text-[#C6FF00] hover:border-[#C6FF00]/30 transition-all flex items-center justify-center" title="Android"><AndroidIconCustom size={18} /></div>;
                      if (lowerOs.includes('ios') || lowerOs.includes('apple') || lowerOs.includes('mac')) return <div key={os} className="w-10 h-10 rounded-2xl bg-white/5 border border-white/10 text-white hover:text-[#C6FF00] hover:border-[#C6FF00]/30 transition-all flex items-center justify-center" title="Apple"><AppleIconCustom size={18} /></div>;
                      return <span key={os} className="px-3 py-1 bg-white/5 rounded text-[9px] text-white/40 border border-white/10 uppercase font-bold">{os}</span>;
                    }) || (
                      <div className="flex gap-4">
                        <div className="w-10 h-10 rounded-2xl bg-white/5 border border-white/10 text-white flex items-center justify-center"><WindowsIconCustom size={18} /></div>
                        <div className="w-10 h-10 rounded-2xl bg-white/5 border border-white/10 text-white flex items-center justify-center"><AndroidIconCustom size={18} /></div>
                        <div className="w-10 h-10 rounded-2xl bg-white/5 border border-white/10 text-white flex items-center justify-center"><AppleIconCustom size={18} /></div>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-white/20 text-[9px] font-black uppercase tracking-[0.2em] mb-4">Stack Tags</div>
                  <div className="flex flex-wrap gap-2">
                    {app.technologies?.map((tech, idx) => (
                      <span key={idx} className="text-[9px] font-black uppercase tracking-tighter text-[#C6FF00] bg-[#C6FF00]/5 px-3 py-1 rounded-full border border-[#C6FF00]/20">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <button className="w-full py-5 rounded-[2rem] bg-white/[0.02] border border-white/5 text-white/20 hover:text-white hover:border-white/20 transition-all text-[10px] font-black uppercase tracking-[0.4em]">
              Report Signal Error
            </button>
          </aside>
        </div>
      </div>
    </div>
  );
};